// A thin wrapper around Google Analytics event tracking
import Cookie from '@wetransfer/concorde-cookie';

import { TrackJS } from 'trackjs';

import { AppConfig } from '@app/core-bundle/config/app-config';

import scriptLoader from '@helper/script-loader';

import AdZerkPixel from '@constant/tracking/adzerk';
import AmazonPixel from '@constant/tracking/amazon';
import AppNexusPixel from '@constant/tracking/app-nexus';
import FacebookScript from '@constant/tracking/facebook';
import TeadsScript from '@constant/tracking/teads';

import LaunchDarkly from '@module/launchdarkly';
import { FF_PIXEL_TRACKING } from '@constant/experiments';

export const defaultSettings = {
  referralCookieName: AppConfig.cookies.referral.name,
  firstVisitCookieName: AppConfig.cookies.firstVisit.name,
  allowTracking: false,
  userId:
    (window &&
      window.__session__ &&
      window.__session__.user &&
      window.__session__.user.id) ||
    null,
  domain:
    (window &&
      window.__session__ &&
      window.__session__.settings &&
      window.__session__.settings.cookie_domain) ||
    (location && location.domain) ||
    'wetransfer.com',
  sdks: AppConfig.analytics.sdks,
};

// Module State
let state = {
  settings: { ...defaultSettings },
  firstPageView: true,
  dataLayerQueue: [],
  pixelOrSDKQueue: [],
  SDKsLoaded: false,
};

const Analytics = {
  state,

  configure,

  getReferralKey,
  setReferralKey,

  // Tracking functions
  trackSnowplowEvent,
  trackGoogleEvent,
  trackWithPixelOrSDK,
  trackPageView,
  trackFirstVisit,
  trackConsentGranted,
};

/**
 * Configure Analytics
 * @param    {Object}   [opts=defaultSettings]       Configuration
 * @property {String}   [opts.referralCookieName]    Referral Cookie Name
 * @property {String}   [opts.firstVisitCookieName]  First Visit Cookie Name
 * @property {Boolean}  [opts.allowTracking=false]   Allow user tracking
 * @property {Number}   [opts.userId]                The Users' ID
 * @property {String}   [opts.domain=wetransfer.com] The platform domain
 * @property {Object}   [opts.sdks]                  Vendor SDK's
 * @property {String}   [opts.sdks.vendor]           Vendor script url
 * @param    {Object}   [state]                      Override module state
 */
async function configure(opts = {}, _state) {
  if (_state) {
    state = _state;
  }

  state.settings = {
    ...state.settings,
    ...opts,
  };

  // If the user id is available we add it to snowplow
  if (state.settings.userId && isSnowPlowAvailable()) {
    window.__snowplow__('setUserId', state.settings.userId);
  }

  // Try to load SDK's if allowed
  if (isTrackingAllowed() && !state.SDKsLoaded) {
    try {
      // First wait for the SDK's to load
      await Promise.all(
        Object.values(state.settings.sdks).map((script) => scriptLoader(script))
      );

      state.SDKsLoaded = true;
    } catch (error) {
      TrackJS.track(error);
    }
  }

  // Try to clear the Google queues
  clearDataLayerQueue();

  // Try to clear the Pixel/SDK queues (after SDK's are resolved)
  clearPixelOrSDKQueue();
}

/**
 * Push an event to Snowplow
 * @param    {Object} eventData            Data to send
 * @property {String} eventData.category   Event Category
 * @property {String} eventData.action     Event Action
 * @property {String} [eventData.label]    Event Label
 * @property {String} [eventData.property] Event Property
 */
function trackSnowplowEvent({ category, action, label, property } = {}) {
  // https://github.com/snowplow/snowplow/wiki/2-Specific-event-tracking-with-the-Javascript-tracker#371-trackStructEvent
  if (!isSnowPlowAvailable()) {
    return;
  }

  if (!category || !action) {
    throw new Error('Both "Category" and "Action" are required');
  }

  window.__snowplow__(
    'trackStructEvent:co',
    category,
    action,
    label, //optional
    property //optional
  );
}

/**
 * Push an event to Google Analytics
 * let's try to avoid this as much as possible,
 * since we value our users' data and prefer to keep it in Snowplow
 * @param {Object} eventData Data to send
 */
function trackGoogleEvent(eventData = {}, type = 'event') {
  /*
   * The following conditions NEED to be met in order to enable GTM tracking
   * wt_tandc cookie value starts with '2'
   * the 'termsAccepted' event has been sent.
   * (this is set in the GTM container)
   */

  // Prevent the initial page load from firing pageView event
  // They are handled by the google script loading
  if (type === 'pageview' && state.firstPageView) {
    state.firstPageView = false;
    return;
  }

  // Store event in a queue when the dataLayer is not available yet
  if (!isDataLayerAvailable()) {
    state.dataLayerQueue.push([eventData, type]);
    return;
  }

  // First resolve queued events if there are any
  clearDataLayerQueue();

  // Add missing fields fallbacks
  const payload = {
    action: undefined,
    label: undefined,
    event: type,
    category: undefined,
    virtualPageLocation: undefined,
    virtualPageTitle: undefined,
    virtualPagePath: undefined,
    value: undefined,
    variant: undefined,
    transfer: undefined,
    ...eventData,
  };

  // Send out the actual event to the dataLayer
  window.dataLayer.push(payload);
}

/**
 * Load a tracking pixel or fire SDK action
 * let's try to avoid this as much as possible,
 * since we value our users' data and prefer to keep it in Snowplow
 * @param {String} event    Event to attach to tracker
 * @param {Object} [params] Parameters to attach to tracker
 */
function trackWithPixelOrSDK(event, params = {}) {
  // Store event in a queue when the pixel/SDK is not available yet
  if (!isPixelTrackingAllowed() || !state.SDKsLoaded) {
    state.pixelOrSDKQueue.push([event, params]);

    return;
  }

  // First resolve queued events if there are any
  clearPixelOrSDKQueue();

  // There are 2 ways firing pixel tracker.
  // 1. Load pixel image with tracking param. e.g: <img src=".../px?id=1121947">
  // 2. Call a script provided by SDK. e.g: fbq('track', 'CustomizeProduct');

  const pixels = [];
  const scripts = [];

  if (event in AdZerkPixel) {
    pixels.push(AdZerkPixel[event]);
  }

  if (event in AmazonPixel) {
    pixels.push(AmazonPixel[event]);
  }

  if (event in AppNexusPixel) {
    pixels.push(AppNexusPixel[event]);
  }

  if (event in FacebookScript) {
    scripts.push(FacebookScript[event]);
  }

  if (event in TeadsScript) {
    scripts.push(TeadsScript[event]);
  }

  scripts.forEach((script) => {
    try {
      script(params);
    } catch (error) {
      TrackJS.track(error);
    }
  });

  pixels.forEach((src) => {
    document.body.appendChild(createTrackingPixel(src));
  });
}

/**
 * Record a user's first visit to the website (timestamp)
 * @param {String} [cookieName] The name of the cookie
 */
function trackFirstVisit(cookieName = state.settings.firstVisitCookieName) {
  if (!Cookie.get(cookieName)) {
    Cookie.set(cookieName, Date.now(), {
      days: 365,
      domain: state.settings.domain,
    });
  }
}

/**
 * Register a page view event.
 * If it's first page view, it will be ignored.
 * Window will send the event on page load
 * @param {String} path Navigation path
 */
function trackPageView(path) {
  const url = new URL(`${location.origin}${path}`);

  // Snowplow
  if (isSnowPlowAvailable()) {
    window.__snowplow__('trackPageView:co', `${url.pathname}${url.search}`, [
      {
        schema: 'iglu:com.wetransfer/launchdarkly/jsonschema/1-0-0',
        data: {
          userKey:
            (window.__launch_darkly__ &&
              window.__launch_darkly__ &&
              window.__launch_darkly__.user &&
              window.__launch_darkly__.user.key) ||
            '',
        },
      },
    ]);
  }

  // Google Analytics
  Analytics.trackGoogleEvent(
    {
      virtualPageTitle: window.document.title,
      virtualPagePath: url.pathname,
    },
    'pageview'
  );

  // Pixel or SDK
  if (url.pathname === '/') {
    Analytics.trackWithPixelOrSDK('Wetransfer_site_visit');
  }
  if (url.pathname === '/pro') {
    Analytics.trackWithPixelOrSDK('WeTransfer_Plus_Homepage_Visit');
  }
}

/**
 * Register the user giving tracking consent.
 * @param    {Object} params         Document parameters
 * @property {String} params.id      Document ID
 * @property {String} params.version Document Version
 * @property {String} params.name    Document Name
 */
function trackConsentGranted({ id, version, name }) {
  if (isSnowPlowAvailable()) {
    window.__snowplow__(
      'trackConsentGranted:co',
      id, // Consent document id
      version, // Consent document version
      name, // Consent document name
      null, // Consent document description
      null, // Expiry of consent
      null, // Custom context for the event
      Date.now() // When the acceptence occurred
    );
  }

  // TODO
  // When a Cookie Banner is implemented
  // we should also tell GA that the user has given consent for tracking,
  // not that the user has accepted the terms (incorrect flow)
  Analytics.trackGoogleEvent(
    {
      virtualPagePath: `${location.pathname}${location.search}`,
      virtualPageLocation: `${location}`,
    },
    'termsAccepted'
  );
}

/**
 * Get the previous referral key from the cookies
 * @return {String} Referral Key
 */
function getReferralKey() {
  return Cookie.get(state.settings.referralCookieName);
}

/**
 * Store the referral key
 * @param {String} Params URL Search Parameters
 */
function setReferralKey(params) {
  // ==============================================================
  // Acquisition Tracker - Referral Key

  // User Growth & Analytics uses Referral key to segment traffic from
  // multiple acquisition channels on WT site.
  // (e.g https://wetransfer.com/pro?trk=WT201610_HalfPanel_GotPlusClick)

  // WeTransfer's acquisition channels include but not limit to:
  // 1. Wallpaper CTA clicks
  // 2. Splash page CTA clicks (param might change based on experiments)
  // 3. Silent redirect to sign-in page on 3rd visit to home page
  // 4. Plus page CTA clicks

  // If a user clicks on one of the acquisition CTA then a referral key is
  // attached to that user for 1 hour. If user sign up during that period we
  // will store the referral key with the sign up records. An ETL job will
  // store it into Redshift which enables us to create acquisition reports
  // like https://metabase.wetransfer.net/question/1914

  // Please assign @azclub for review if you need to change
  // ==============================================================
  if (Cookie.get(state.settings.referralCookieName)) {
    return;
  }

  // Keep `trk` param persistent in cookie for 1 hour.
  const referral_key = new URLSearchParams(params).get('trk');
  if (!referral_key) {
    return;
  }

  const expires = new Date(Date.now() + 60 * 60 * 1000);
  Cookie.set(state.settings.referralCookieName, referral_key, {
    expires,
  });
}

// --- Helpers
function isDataLayerAvailable() {
  // GTM sets a `google_tag_manager` variable on the global scope that
  // informs us if it is loaded or not.
  return (
    window &&
    window.dataLayer &&
    window.google_tag_manager &&
    window.google_tag_manager.dataLayer &&
    window.google_tag_manager.dataLayer.gtmLoad
  );
}

function isSnowPlowAvailable() {
  return window && typeof window.__snowplow__ === 'function';
}

function isTrackingAllowed() {
  return (
    state.settings.allowTracking && location.pathname !== '/forgot-password'
  );
}

function isPixelTrackingAllowed() {
  return (
    isTrackingAllowed() &&
    LaunchDarkly &&
    LaunchDarkly.getVariant(FF_PIXEL_TRACKING)
  );
}

function clearDataLayerQueue() {
  if (state.dataLayerQueue.length) {
    const event = state.dataLayerQueue[state.dataLayerQueue.length - 1];
    state.dataLayerQueue = state.dataLayerQueue.slice(0, -1);
    Analytics.trackGoogleEvent(...event);
  }
}

function clearPixelOrSDKQueue() {
  if (state.pixelOrSDKQueue.length) {
    const event = state.pixelOrSDKQueue[state.pixelOrSDKQueue.length - 1];
    state.pixelOrSDKQueue = state.pixelOrSDKQueue.slice(0, -1);
    Analytics.trackWithPixelOrSDK(...event);
  }
}

// --- Exports
export default Analytics;

/**
 * Create a tracking pixel.
 * @param  {String} src       Image web-address
 * @return {HTMLImageElement} Pixel image element
 */
export function createTrackingPixel(src) {
  const pixel = new Image(1, 1);
  pixel.border = 0;
  pixel.alt = '';
  pixel.src = src;

  return pixel;
}
